import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    EditTask,
    Task,
    TaskComment, TaskConfigCategory,
    TaskConfigField,
    TaskContentResponses,
    TaskFieldContent,
    TaskHistory,
    TaskSwimLane,
} from '@app/interfaces/lm/kanban-board';
import { Auth } from '@app/interfaces/sys/auth';
import { SelectOption } from '@app/interfaces/sys/dict';
import { KanbanBoardService } from '@services/lm/kanban-board.service';
import { AccountService } from '@services/sys/account.service';
import { ConfirmationService } from '@services/sys/confirmation.service';
import { TokenService } from '@services/sys/token.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import dayjs from 'dayjs';
import { DocumentFile } from '@app/interfaces/dm/document';
import { Account } from '@app/interfaces/sys/account';
import { CdkVirtualScrollViewport, CdkScrollable } from '@angular/cdk/scrolling';
import { TaskCreateComponent } from '@app/task-shared/components/task-detail/components/task-create/task-create.component';
import { AddTaskLinkComponent } from '@app/task-shared/components/task-detail/components/add-task-link/add-task-link.component';
import { InputContentComponent } from '@app/task-shared/components/task-detail/components/input-content/input-content.component';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { LabelSelectComponent } from './components/task-fields/components/label-select/label-select.component';
import { AssignMemberComponent } from './components/task-fields/components/assign-member/assign-member.component';
import { DateSelectComponent } from './components/task-fields/components/date-select/date-select.component';
import { ZSelectComponent } from './components/task-fields/components/z-select/z-select.component';
import { TaskFieldsComponent } from './components/task-fields/task-fields.component';
import { FieldItemComponent } from './components/task-fields/components/field-item/field-item.component';


@Component({
    selector: 'app-task-detail',
    templateUrl: './task-detail.component.html',
    styleUrl: './task-detail.component.less',
})
export class TaskDetailComponent implements OnInit, OnDestroy {
    @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
    @ViewChild('addTask') addTask!: TaskCreateComponent;
    @ViewChild('addTaskLink') addTaskLink!: AddTaskLinkComponent;
    @ViewChild('descCom') descCom!: InputContentComponent;
    @ViewChildren(NzSelectComponent) selects!: QueryList<NzSelectComponent>;
    @ViewChildren(NzDatePickerComponent) datePickers!: QueryList<NzDatePickerComponent>;
    @ViewChildren(LabelSelectComponent) labelSelects!: QueryList<LabelSelectComponent>;
    @ViewChildren(AssignMemberComponent) assignMembers!: QueryList<AssignMemberComponent>;
    @ViewChildren(DateSelectComponent) dateSelects!: QueryList<DateSelectComponent>;
    @ViewChildren(ZSelectComponent) zSelects!: QueryList<ZSelectComponent>;
    @ViewChildren(FieldItemComponent) fieldItems!: QueryList<FieldItemComponent>;
    @ViewChild('taskFieldsCom') taskFieldsCom!: TaskFieldsComponent;
    @Input() taskKey = '';
    @Input() fullscreen = true;
    @Output() closeEvent = new EventEmitter();
    @Output() changeDetail = new EventEmitter();
    @Output() changeSwimLane = new EventEmitter();
    @Output() refreshStatus = new EventEmitter();

    isVisible = false;
    detail: Task = {} as Task;
    projectKey = '';
    currentActivityType = 'Comments';
    activityTypeOptions = ['All', 'Comments', 'History'];
    activitySort = 'desc';
    userInfo!: Auth;
    loading = false;
    currentTaskKey = '';
    projectId = '';
    taskComments: TaskComment[] = [];
    taskHistories: TaskHistory[] = [];
    activityLoading = false;
    subTask: Task[] = [];
    swimLaneList: TaskSwimLane[] = [];
    descLoading = false;
    accountList: Account[] = [];
    projectMembers: SelectOption[] = [];
    allUsers: SelectOption[] = [];
    hasInactiveUsers: boolean = false;

    descriptionFields: TaskConfigField[] = [];
    defaultFields: TaskConfigField[] = [];
    moreFields: TaskConfigField[] = [];
    fileList: DocumentFile[] = [];
    defaultAttachmentField!: TaskConfigField | undefined;   // 默认field的字段id
    uploadLoading = false;
    taskAttachList: string[] = [];
    showAttachList = false;
    templateAllFields: TaskConfigField[] = [];
    pluralCategories: string[] = [];  // 复数类型的category
    size = 20;
    page = 0;
    historyTotal = 0;
    hasTimeCategories: string[] = ['Resource', 'Tester'];
    private scrollStartTop: { [key: string]: number | null } = {};
    private scrollThreshold = 50;

    constructor(private cdr: ChangeDetectorRef,
        private tokenService: TokenService,
        private kanbanBoardService: KanbanBoardService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private routeLocation: Location,
        private accountService: AccountService,
        private message: NzMessageService,
        private modal: NzModalService,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService) {
    }

    // 是否是子任务
    get isSubTask(): boolean {
        return !!this.detail.subTaskId;
    }

    get taskId(): string {
        return this.isSubTask ? this.detail?.subTaskId as string : this.detail?.taskId as string;
    }

    async ngOnInit(): Promise<void> {
        // 监听参数变化
        this.route.paramMap.subscribe(params => {
            this.fetchData();
        });
    }

    async fetchData(): Promise<void> {
        try {
            this.userInfo = this.tokenService.get();
            if (this.taskKey) {
                this.currentTaskKey = this.taskKey;
            } else {
                this.currentTaskKey = this.activatedRoute.snapshot.params.taskKey;
            }
            console.log('currentTaskKey', this.currentTaskKey);
            this.loading = true;
            await this.fetchAllAccount();
            await this.initData();
            await Promise.all([this.getSwimLaneList(), this.fetchTaskFields(), this.refreshActivity()]);
            this.getAttachList();
        } finally {
            this.loading = false;
        }
    }

    getAttachList(): void {
        const { taskContentResponses } = this.detail;
        if (!this.defaultAttachmentField) {
            return;
        }
        const valueItem = (taskContentResponses || []).find(it => it.taskConfigFieldId === (this.defaultAttachmentField as TaskConfigField).id);
        if (!valueItem) {
            return;
        }
        this.taskAttachList = valueItem.value as string[];
        this.showAttachList = this.taskAttachList.length > 0;
        console.log('taskAttachList', this.taskAttachList);
    }

    async fetchTaskFields(): Promise<void> {
        const configId = this.detail.configId;
        if (!configId) {
            return;
        }
        const categoryInfos = await this.getCategoryInfos();
        let arr: TaskConfigField[] = (await this.kanbanBoardService.getTaskConfigFieldAll(configId)).map(it => {
            return {
                ...it,
                categoryIsPlural: categoryInfos?.[it.categoryId as string],
            };
        });
        arr.sort((a, b) => a.fieldOrder - b.fieldOrder);
        arr = arr.filter(it => !this.kanbanBoardService.isDefaultField(it.defaultValueRule) || !['Status'].includes(it.fieldName));
        this.descriptionFields = arr.filter(it => it.panel === 'left').map(it => {
            const { defaultValueRule } = it;
            const isDefaultField = this.kanbanBoardService.isDefaultField(defaultValueRule);
            const defaultValueRuleData = defaultValueRule ? JSON.parse(defaultValueRule) : {};
            return {
                ...it,
                fieldKey: isDefaultField ? defaultValueRuleData.fieldKey : '',
            };
        });
        this.defaultFields = arr.filter(it => it.panel === 'right_top');
        this.moreFields = arr.filter(it => it.panel === 'right_bottom');
        const attachmentField = arr.find(it => this.kanbanBoardService.isDefaultField(it.defaultValueRule) && it.categoryName === 'Attachment');
        this.defaultAttachmentField = attachmentField;
        this.templateAllFields = [...this.descriptionFields, ...this.defaultFields, ...this.moreFields];
        console.log('descriptionFields', this.templateAllFields);
    }

    async getCategoryInfos(): Promise<{
        [categoryId: string]: boolean
    }> {
        const allCategories = await this.kanbanBoardService.getAllCategory();
        this.pluralCategories = allCategories.filter(it => it.isPlural).map(x => x.categoryName);
        let data = {};
        allCategories.forEach(it => {
            data = {
                ...data,
                [it.id]: it.isPlural,
            };
        });
        return data;
    }

    ngOnDestroy(): void {
    }

    async getProjectDetail(): Promise<void> {
        if (!this.projectKey) {
            return;
        }
        const data = await this.kanbanBoardService.getKanbanProjectDetail(this.projectKey);
        const { projectMembers } = data;
        const members = this.kanbanBoardService.getMembers(projectMembers, this.accountList);
        this.projectMembers = this.removeInactiveUsers(members);
    }

    removeInactiveUsers(list: SelectOption[]): SelectOption[] {
      return list.filter(item => item.enable);
    }

    async getSwimLaneList(): Promise<void> {
        const arr = await this.kanbanBoardService.getSwimLanesByProjectId(this.projectId);
        this.swimLaneList = arr;
    }

    async getTaskDetail(): Promise<void> {
        const task = (await this.kanbanBoardService.getTaskDetailByKey(this.currentTaskKey));
        this.detail = this.refreshTaskData([task])[0];
        console.log('task-detail', this.detail);
        this.subTask = [];
        if (task.hasSubtask) {
            await this.getTaskAllSubTask();
        }
        if (task.parentTaskKey) {
            this.projectId = (await this.kanbanBoardService.getTaskDetailByKey(task.parentTaskKey as string)).projectId;
        }
        else {
            this.projectId = task.projectId;
        }
        this.getAttachList();
    }

    async onLoadMore(): Promise<void> {
        const endOfScroll = this.historyTotal === this.taskHistories.length;
        if (!endOfScroll && !this.activityLoading) {
            this.page++;
            await this.getTaskHistories();
        }
    }

    async initData(): Promise<void> {
        await this.getTaskDetail();
        this.projectKey = this.getProjectKey(this.detail.taskKey);
        this.getProjectDetail();
    }

    getProjectKey(taskKey: string): string {
        const separatorIndex = (taskKey || '')?.indexOf('-');
        return separatorIndex !== -1 ? taskKey.substring(0, separatorIndex) : taskKey;
    }

    async getTaskAllSubTask(): Promise<void> {
        const params = {
            parentTaskId: this.taskId,
            showArchived: true,
        };
        const arr = await this.kanbanBoardService.getTaskAllSubTask(params);
        const formatedArr = this.refreshTaskData(arr);
        this.subTask = formatedArr;
    }

    async updateTaskComments(addedFiles: string[]): Promise<void> {
        if (addedFiles && addedFiles.length > 0) {
            const taskFieldContents = this.updateAttachment(addedFiles);
            await this.updateTask({
                taskFieldContents,
            });
        }
        await this.getTaskComments();
    }

    async saveComment(data: { commentInfo: TaskComment, addedFiles: string[]; }, commentIndex?: number): Promise<void> {
        const { commentInfo, addedFiles } = data;
        // edit task comment
        if (commentIndex || commentIndex === 0) {
            this.taskComments[commentIndex] = commentInfo;
        } else {
            this.taskComments.splice(0, 0, commentInfo);
        }
        if (addedFiles && addedFiles.length > 0) {
            const taskFieldContents = this.updateAttachment(addedFiles);
            await this.updateTask({
                taskFieldContents,
            });
        }
    }

    deleteComment(commentIndex: number): void {
        this.taskComments.splice(commentIndex, 1);
    }

    async getTaskComments(): Promise<void> {
        try {
            if (!this.taskId) {
                return;
            }
            this.activityLoading = true;
            const comments = await this.kanbanBoardService.getTaskComment(this.taskId);
            this.taskComments = this.sortDate(comments) as TaskComment[];
        } finally {
            this.activityLoading = false;
        }
    }

    async getTaskHistories(): Promise<void> {
        try {
            if (!this.taskId) {
                this.taskHistories = [];
                return;
            }
            this.activityLoading = true;
            const params = this.isSubTask ? {
                subTaskId: this.taskId,
            } : {
                taskId: this.taskId,
                subTaskIdIsNull: true
            };
            const res = await this.kanbanBoardService.getTaskHistoryPage({
                page: this.page,
                size: this.size,
                sort: `lastModifiedDate,${this.activitySort}`,
                ...params
            });
            const { content, totalElements } = res;
            this.historyTotal = totalElements;
            this.taskHistories.push(...content);
        } finally {
            this.activityLoading = false;
        }
    }

    sortDate(arr: TaskHistory[] | TaskComment[]): Array<TaskComment | TaskHistory> {
        const isNewBefore = this.activitySort === 'desc';  // 新的在前
        return (arr || []).sort((a, b) =>
            isNewBefore ? dayjs(b.createdDate).valueOf() - dayjs(a.createdDate).valueOf() :
                dayjs(a.createdDate).valueOf() - dayjs(b.createdDate).valueOf(),
        );
    }

    open(task: Task): void {
        this.userInfo = this.tokenService.get();
        this.detail = { ...task };
        this.isVisible = true;
    }

    close(): void {
        if (this.fullscreen) {
            this.routeLocation.back();
        } else {
            this.closeEvent.emit();
        }
    }

    switchFullscreen(): void {
        this.router.navigateByUrl(`/kanban-board/${this.projectKey}/task/${this.taskKey}`);
    }

    changeActivityType(activityType: string): void {
        this.currentActivityType = activityType;
        this.refreshActivity();
    }

    async refreshActivity(): Promise<void> {
        this.taskComments = [];
        switch (this.currentActivityType) {
            case 'Comments':
                await this.getTaskComments();
                break;
            case 'History':
                await this.refreshHistories();
                break;
            default:
                await Promise.all([this.getTaskComments(), this.refreshHistories()]);
                break;
        }
    }

    async refreshHistories(): Promise<void> {
        this.page = 0;
        this.historyTotal = 0;
        this.taskHistories = [];
        await this.getTaskHistories();
    }

    changeActivitySort(): void {
        if (this.activitySort === 'desc') {
            this.activitySort = 'asc';
        } else {
            this.activitySort = 'desc';
        }
        // this.taskHistories = this.sortDate(this.taskHistories) as TaskHistory[];
        this.taskComments = this.sortDate(this.taskComments) as TaskComment[];
        if (this.currentActivityType !== 'Comments') {
            this.page = 0;
            this.historyTotal = 0;
            this.taskHistories = [];
            this.getTaskHistories();
        }
    }

    async fetchAllAccount(): Promise<void> {
        const query = {
            page: 0,
            size: 999999,
        };
        const { content } = await this.accountService.find(query);
        this.accountList = content;
    }

    async changeTaskName(newValue: string): Promise<void> {
        if (newValue.length > 100) {
            this.message.error('Task name length cannot exceed 100 characters');
            return;
        }
        await this.updateTask({
            taskName: newValue,
        });
    }

    // 新增附件
    async saveAttach(newFile: DocumentFile[]): Promise<void> {
        try {
            console.log('saveAttach', newFile);
            this.uploadLoading = true;
            const { taskContentResponses } = this.detail;
            const newFileItems = this.kanbanBoardService.formatFileList(newFile);
            const newValue = [
                ...this.taskAttachList,
                ...newFileItems
            ];
            const data = this.kanbanBoardService.getTaskFieldContents(this.defaultAttachmentField as TaskConfigField, newValue, this.templateAllFields, taskContentResponses);
            await this.updateTask({
                taskFieldContents: data,
            });
        } finally {
            this.uploadLoading = false;
        }
    }

    async updateTask(updateFieldsForm: any): Promise<void> {
        const params = {
            ...this.detail,
            id: this.taskId,
            labels: this.detail.labels.map(it => it.id),
            taskFieldContents: this.detail.taskContentResponses.map(it => {
                return {
                    taskConfigFieldId: it.taskConfigFieldId,
                    value: it.value,
                };
            }),
            ...updateFieldsForm,
        } as EditTask;
        this.changeDetail.emit('update');
        this.isSubTask ? await this.kanbanBoardService.editSubTask(params) :
            await this.kanbanBoardService.editTask(params);
        await Promise.all([this.getTaskDetail(), this.refreshActivity()]);
    }

    async saveDesc(data: {
        value: string,
        newFiles: string[]
    }): Promise<void> {
        const { value, newFiles } = data;
        if (value.length > 10000) {
            this.message.error('Task description length cannot exceed 10000 characters');
            return;
        }
        const taskFieldContents = this.updateAttachment(newFiles);
        try {
            this.descLoading = true;
            await this.updateTask({
                description: value,
                taskFieldContents,
            });
        } finally {
            this.descLoading = false;
        }
    }

    /* 更新描述或者评论字段上传文件 */
    updateAttachment(newFiles: string[]): TaskFieldContent[] {
        let taskFieldContents: TaskContentResponses[] = [];
        if (newFiles.length > 0 && this.defaultAttachmentField) {
            const { taskContentResponses } = this.detail;
            taskFieldContents = taskContentResponses;
            const fieldId = (this.defaultAttachmentField as TaskConfigField).id;
            const fieldIndex = taskFieldContents.findIndex(it => it.taskConfigFieldId === fieldId);
            if (fieldIndex !== -1) {
                const oldFiles = taskFieldContents[fieldIndex].value || [];
                taskFieldContents[fieldIndex] = {
                    ...taskFieldContents[fieldIndex],
                    value: [...oldFiles, ...newFiles],
                };
            }
        }
        return taskFieldContents;
    }

    addSubTask(): void {
        this.confirmationService.confirmUpdateTask(this.hasInactiveUsers, () => {
            this.addTask.open();
        }, () => { this.addTask.close(); });
    }

    deleteTask(): void {
        if (this.isSubTask) {
            this.removeSubTask(this.detail);
        }
        if (this.detail.hasSubtask) {
            this.message.error('Please delete all subtasks before proceeding with this action.');
            return;
        } else if (!this.isSubTask) {
            this.modal.confirm({
                nzTitle: 'The Task will be deleted.',
                nzOkText: 'Yes',
                nzCancelText: 'No',
                nzOkType: 'primary',
                nzOkDanger: true,
                nzStyle: { top: '40vh' },
                nzOnOk: async () => {
                    const swimLaneId = this.detail.swimLaneId;
                    await this.kanbanBoardService.removeTask(this.detail.taskId);
                    this.message.success('Deleted successfully');
                    if (this.fullscreen) {
                        this.router.navigateByUrl(`/kanban-board/${this.projectKey}`);
                    } else {
                        this.changeDetail.emit({ action: 'delete', swimLaneId });
                        this.closeEvent.emit();
                    }
                },
            });
        }
    }


    linkTask(): void {
        this.confirmationService.confirmUpdateTask(this.hasInactiveUsers, () => {
            this.addTaskLink.open();
        }, () => { this.addTaskLink.close(); });
    }

    navigateToParent(): void {
        this.router.navigateByUrl(`/kanban-board/${this.projectKey}/task/${this.detail.parentTaskKey}`);
    }

    async removeLinkTask(index: number, item: Task): Promise<void> {
        this.modal.confirm({
            nzTitle: 'The linked task will be removed',
            nzOkText: 'Yes',
            nzCancelText: 'No',
            nzOkType: 'primary',
            nzOkDanger: true,
            nzStyle: { top: '40vh' },
            nzOnOk: () => {
                this.confirmationService.confirmUpdateTask(this.hasInactiveUsers, async () => {
                    try {
                        this.loading = true;
                        const linkedTasks = [...(this.detail.linkedTasks || [])];
                        linkedTasks.splice(index, 1);
                        const data = {
                            taskId: this.detail.taskId,
                            linkedTaskIds: linkedTasks.map(it => it.id),
                        };
                        await this.kanbanBoardService.updateTaskLinkedTask(data);
                        if (this.detail.linkedTasks) {
                            this.detail.linkedTasks.splice(index, 1);
                        }
                    } finally {
                        this.loading = false;
                    }
                });
            },
        });
    }

    async removeSubTask(item: Task): Promise<void> {
        const taskId = item.subTaskId || '';
        if (!taskId) {
            return;
        }
        this.modal.confirm({
            nzTitle: 'The sub task will be removed',
            nzOkText: 'Yes',
            nzCancelText: 'No',
            nzOkType: 'primary',
            nzOkDanger: true,
            nzStyle: { top: '40vh' },
            nzOnOk: () => {
                this.confirmationService.confirmUpdateTask(this.hasInactiveUsers, async () => {
                    try {
                        this.loading = true;
                        await this.kanbanBoardService.removeSubTask(taskId);
                        this.message.success('Deleted successfully');
                        if (!this.isSubTask) {
                            await this.getTaskDetail();
                            await this.refreshActivity();
                        }
                    } finally {
                        this.loading = false;
                        if (this.isSubTask) {
                            this.navigateToParent();
                        }
                    }
                });
            },
        });
    }

    updateTaskInfo(updateItems: {
        field: TaskConfigField,
        fieldKey: string,
        value: any
    }): void {
        const { field, fieldKey, value } = updateItems;
        const { categoryName, id, fieldName, fieldOrder, categoryId } = field;
        if (categoryName === 'Resource') {
            this.detail = {
                ...this.detail,
                resources: value,
            };
        } else if (categoryName === 'Tester') {
            // Rebuild testersInfo with enable status from accountList
            const testersInfo = (value || []).map((tester: any) => {
                const testerId = tester.testerId as number;
                const account = this.accountList.find(a => a.id === testerId);
                return {
                    label: tester.testerName || '',
                    value: testerId,
                    avatar: account ? {
                        label: this.accountService.getUserName(account),
                        enable: account.enable,
                        avatar: account.avatar || undefined
                    } : undefined
                };
            });
            this.detail = {
                ...this.detail,
                testers: value,
                testersInfo,
            };
        } else if (fieldKey) {
            if (fieldKey === 'swimLane') {
                this.getTaskDetail().then(() => {
                    this.cdr.detectChanges();
                });
            } else {
                this.detail = {
                    ...this.detail,
                    [fieldKey]: value,
                };
            }
        } else {
            const { taskContentResponses } = this.detail;
            const valueIsArray = Array.isArray(value);
            const submitValue = (field.categoryIsPlural && !valueIsArray ? (value ? [value] : []) : value);
            const idx = (taskContentResponses || []).findIndex(it => it.taskConfigFieldId === id);
            if (idx === -1) {
                this.detail.taskContentResponses.push({
                    category: categoryName,
                    categoryId,
                    fieldName,
                    fieldOrder,
                    taskConfigFieldId: id,
                    value: submitValue,
                } as TaskContentResponses);
            } else {
                this.detail.taskContentResponses[idx].value = submitValue;
            }
        }
    }

    saveFieldRefresh(updateItems: {
        field: TaskConfigField,
        fieldKey: string,
        value: any
    }): void {
        console.log('updateItems', updateItems);
        if (updateItems.fieldKey === 'swimLane') {
            this.changeSwimLane.emit(updateItems.value);
        } else {
            this.changeDetail.emit('update');
        }
        this.updateTaskInfo(updateItems);
        this.refreshActivity();
    }

    onStatusChange(event: any): void {
        this.refreshStatus.emit('refresh');
    }

    onTaskInfoChange(updatedTaskInfo: Task): void {
        this.detail = updatedTaskInfo;
    }

    refreshTaskData(tasks: Task[]): Task[] {
        return this.kanbanBoardService.formatTasks(tasks, this.accountList);
    }

    copyLink(): void {
        const url = `${window.location.origin}/#/kanban-board/${this.projectKey}/task/${this.detail.taskKey}`;
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(url).then(() => {
                this.message.success('Link copied to clipboard');
            }).catch(err => {
                this.message.error('Failed to copy the link');
            });
        } else {
            // fallback for HTTP or unsupported environments
            const textarea = document.createElement('textarea');
            textarea.value = url;
            textarea.style.position = 'fixed'; // avoid scrolling to bottom
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            try {
                const successful = document.execCommand('copy');
                if (successful) {
                    this.message.success('Link copied to clipboard');
                } else {
                    this.message.error('Failed to copy the link');
                }
            } catch (err) {
                console.error('Fallback clipboard error:', err);
                this.message.error('Failed to copy the link');
            }
            document.body.removeChild(textarea);
        }
    }

    updateInactiveUsers(hasInactiveUsers: boolean): void {
        this.hasInactiveUsers = hasInactiveUsers;
    }

    onContainerScroll(event: Event): void {
        const target = event.target as Element;
        if (!target) return;
        const scrollId = target.className || 'default';
        const currentScrollTop = target.scrollTop ?? 0;

        // Record scroll position at start of scroll
        if (this.scrollStartTop[scrollId] === null || this.scrollStartTop[scrollId] === undefined) {
            this.scrollStartTop[scrollId] = currentScrollTop;
        }

        const scrollDelta = Math.abs(currentScrollTop - this.scrollStartTop[scrollId]!);

        if (scrollDelta >= this.scrollThreshold) {
            this.closeAllDropdowns();
            // Reset after closing so next scroll interaction starts fresh
            this.scrollStartTop[scrollId] = null;
        }
    }

    private closeAllDropdowns(): void {
        // Close all open nz-select dropdowns
        this.selects?.forEach(select => {
            select.setOpenState(false);
        });
        // Close all open date pickers
        this.datePickers?.forEach(picker => {
            picker.close();
        });
        // Close child component dropdowns
        this.labelSelects?.forEach(comp => comp.closeDropdown());
        this.assignMembers?.forEach(comp => comp.closeDropdown());
        this.dateSelects?.forEach(comp => comp.closeDropdown());
        this.zSelects?.forEach(comp => comp.closeDropdown());
        // Close dropdowns in field-item components (left container)
        this.fieldItems?.forEach(item => item.closeAllDropdowns());
        // Close dropdowns in task-fields component (right container)
        this.taskFieldsCom?.closeAllDropdowns();
        // Blur active element for any other inputs
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }
}
